import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import { Vehicle, User, Group } from 'lcmm-lib-js';
import { FuelType, isEnumValueKnown } from 'src/app/utils/utils';

import { NetworkService } from 'src/app/service/network.service';
import { ErrorComponent } from 'src/app/error/error.component';
import { finalize } from 'rxjs';
import { VehicleService } from '../../../service/vehicle.service';
import { VehicleFormComponent } from '../vehicle-form/vehicle-form.component';

@Component({
  selector: 'app-vehicle-dialog',
  templateUrl: './vehicle-dialog.component.html',
  styleUrls: ['./vehicle-dialog.component.scss'],
  standalone: false,
})
export class VehicleDialogComponent implements OnInit {
  private className = 'VehicleDialogComponent';

  @ViewChild(VehicleFormComponent) private vehicleForm: VehicleFormComponent;

  public loading = false;

  public form: UntypedFormGroup;

  public vehicle: Vehicle;

  public isVehicleUpdated: boolean;

  public users: User[];

  public groups: Group[];

  public allowEditing: boolean;

  public isRelocating = false;

  constructor(
    public dialogRef: MatDialogRef<VehicleDialogComponent>,
    private vehicleService: VehicleService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      vehicle: Vehicle;
      users: User[];
      groups: Group[];
      allowEditing: boolean;
    },
    public networkService: NetworkService,
    public errorDialog: MatDialog
  ) {
    // eslint-disable-next-line no-param-reassign
    dialogRef.disableClose = true;

    this.vehicle = null;
    if (data.vehicle) {
      this.vehicle = data.vehicle;
      if (this.vehicle.wltpClass === null) {
        this.vehicle.wltpClass = 0; // Show as invalid
      }
    }

    this.users = null;
    if (data.users) {
      this.users = data.users.filter(
        (user: User) => user.userRole === 'DRIVER'
      );
    }

    this.groups = null;
    if (data.groups) {
      this.groups = data.groups;
    }

    this.allowEditing = data.allowEditing;
  }

  ngOnInit(): void {
    this.form = new UntypedFormGroup({});
    this.isVehicleUpdated = false;
  }

  private _getVehicle(): Vehicle {
    const formValues = this.form.getRawValue();
    const vehicle: Vehicle = formValues.vehicleForm;
    const vehicleUserIdList: string[] = [];

    const vehicleUserList = vehicle.user as unknown as User[];
    if (vehicleUserList) {
      vehicleUserList.forEach((u) => {
        if (u) {
          vehicleUserIdList.push(u.id);
        }
      });
    }

    vehicle.user = vehicleUserIdList;
    vehicle.groupName = this.vehicle.groupName;
    return vehicle;
  }

  private _hasOnlyGroupChanged(): boolean {
    const formValues = this.form.getRawValue().vehicleForm;

    const keysToCheck = Object.keys(formValues).filter((key) => {
      return (
        key !== 'groupName' &&
        !Array.isArray(formValues[key]) &&
        typeof formValues[key] !== 'object'
      );
    });
    return !keysToCheck.some((key) => this.vehicle[key] !== formValues[key]);
  }

  private _canRelocateVehicle(vehicle: Vehicle): boolean {
    if (!vehicle || !vehicle.id) {
      return false;
    }

    return !vehicle.user || vehicle.user.length === 0;
  }

  public onSubmit(): void {
    const targetGroupIdentifier =
      this.vehicleForm?.getTargetGroupForRelocation();
    const hasGroupChanged = targetGroupIdentifier != null;

    const onlyGroupChanged = hasGroupChanged && this._hasOnlyGroupChanged();

    if (onlyGroupChanged) {
      if (this._canRelocateVehicle(this.vehicle)) {
        this._relocateVehicle(this.vehicle, targetGroupIdentifier);
      } else {
        ErrorComponent.open(
          this.errorDialog,
          null,
          {
            title: 'VEHICLE.RELOCATE',
            subtitle: 'VEHICLE.RELOCATE.NO',
            text: 'VEHICLE.RELOCATE.USERS_ASSIGNED',
          },
          true
        );
        this.dialogRef.close(false);
      }
      return;
    }

    const vehicleToUpdate = this._getVehicle();

    this._updateVehicle(vehicleToUpdate, (updatedVehicle) => {
      if (hasGroupChanged) {
        if (this._canRelocateVehicle(updatedVehicle)) {
          this._relocateVehicle(updatedVehicle, targetGroupIdentifier);
        } else {
          ErrorComponent.open(
            this.errorDialog,
            null,
            {
              title: 'VEHICLE.RELOCATE',
              subtitle: 'VEHICLE.RELOCATE.NO',
            },
            true
          );
          this.dialogRef.close(this.isVehicleUpdated);
        }
      } else {
        this.dialogRef.close(this.isVehicleUpdated);
      }
    });
  }

  private _updateVehicle(
    vehicle: Vehicle = null,
    onSuccess: (updatedVehicle: Vehicle) => void = null
  ): void {
    if (!vehicle) {
      // eslint-disable-next-line no-param-reassign
      vehicle = this._getVehicle();
    }

    this.isVehicleUpdated = false;
    this.vehicleService.updateVehicle(vehicle).subscribe(
      (updatedVehicle) => {
        this.isVehicleUpdated = true;

        if (onSuccess) {
          onSuccess(updatedVehicle);
        }
      },
      (error) => {
        const msg = error.error.title
          ? `VEHICLE.UPDATE.${error.error.title}`
          : 'ERROR.OTHER';
        ErrorComponent.open(
          this.errorDialog,
          null,
          {
            title: 'UPDATE',
            subtitle: 'VEHICLE.UPDATE.NO',
            text: msg,
          },
          true
        );
      }
    );
  }

  private _relocateVehicle(
    vehicle: Vehicle,
    targetGroupIdentifier: string
  ): void {
    if (!targetGroupIdentifier || !vehicle || !vehicle.id) {
      this.dialogRef.close(this.isVehicleUpdated);
      return;
    }

    this.isRelocating = true;

    this.vehicleService
      .relocateVehicle(vehicle, targetGroupIdentifier)
      .pipe(
        finalize(() => {
          this.isRelocating = false;
        })
      )
      .subscribe(
        () => {
          this.isVehicleUpdated = true;
          this.dialogRef.close(this.isVehicleUpdated);
        },
        (error) => {
          const errorMessage = error.message;
          ErrorComponent.open(
            this.errorDialog,
            null,
            {
              title: 'VEHICLE.RELOCATE',
              subtitle: 'VEHICLE.RELOCATE.NO',
              text: errorMessage,
            },
            true
          );
          this.dialogRef.close(this.isVehicleUpdated);
        }
      );
  }

  public onClose(): void {
    this.dialogRef.close(this.isVehicleUpdated);
  }

  public isFuelValueOk(): boolean {
    return isEnumValueKnown(FuelType, this.form.value, 'vehicleForm.fuelValue');
  }
}
